import './application.css'
import { createApp } from 'vue'
import { useAuth } from '../composables/useAuth.js'
import { stripAuthParamsFromUrl } from '../lib/authUrlCleanup.js'

// App and router are imported DYNAMICALLY, inside boot(), and that is the whole
// point of this file's shape.
//
// `createWebHistory()` snapshots window.location when the router module is
// EVALUATED, and vue-router replays that snapshot through history.replaceState
// on its first navigation. A static `import router from '../router'` is
// evaluated before any of our code runs — so with Keycloak's callback params
// still on the URL, the router captured them, our scrub cleaned the address bar,
// and the router's first navigation put them straight back. The scrub ran every
// time and lost the race, silently: it is a pure string function, and every unit
// test of it passed.
//
// Found in ginzamarkets/velocity (#409 shipped the scrub, #415 fixed this race).
// ds-agents had the identical shape.
async function boot(el) {
  // Skip Keycloak in test mode — only allowed on hostnames that can never
  // reach a real user: localhost (dev) and the e2e-web-* containers Jenkins
  // spins up in its docker-compose-like network for Playwright runs.
  // Production/staging hostnames (v6-app.demandsphere.com, ds-agents-staging…)
  // never match either pattern, so accidental ?skip_auth links can't disable
  // auth in front of a real user.
  const hostname = window.location.hostname
  const params = new URLSearchParams(window.location.search)
  const isDev = hostname === 'localhost' || hostname === '127.0.0.1' || /^e2e-web-/.test(hostname)
  if (params.has('skip_auth') && isDev) {
    localStorage.setItem('skip_auth', 'true')
  }
  const basePath = import.meta.env.VITE_BASE_PATH || '/agents'
  const isPublicRoute = window.location.pathname.startsWith(`${basePath}/share/`)

  if (!isPublicRoute) {
    // 1. Auth FIRST. keycloak-js needs the callback params ON the URL to parse
    //    them; scrubbing before this breaks login outright.
    //
    //    useAuth().init() short-circuits on skip_auth localStorage by populating
    //    a fake vikinglord user (so /vikinglord/* router guards resolve),
    //    otherwise runs the real keycloak-js handshake.
    const { init } = useAuth()
    await init()

    // 2. Then scrub. keycloak-js only strips its own params when a code AND a
    //    state are both present, so a URL left carrying state/session_state/iss
    //    keeps them, in the address bar and in browser history.
    stripAuthParamsFromUrl()
  }

  // 3. ONLY NOW construct the router, so createWebHistory() snapshots a URL that
  //    has already been cleaned.
  const [{ default: App }, { default: router }] = await Promise.all([
    import('../App.vue'),
    import('../router/index.js'),
  ])

  const app = createApp(App)
  app.use(router)
  app.mount(el)
}

const el = document.getElementById('app')
if (el) boot(el)
